import React from "react";
import { motion } from "framer-motion";
import { Boxes, Factory, Grid3x3, Container, Weight, Ruler, Activity, CheckCircle2 } from "lucide-react";
import { fadeUp, viewport } from "@/lib/motion";
import { CONTAINER_PRODUCTS } from "@/data/smartContainer";
import SectionHeader from "./SectionHeader";

const MANUFACTURERS = [
  { name: "Anatolia Foods", lines: 4 },
  { name: "Marmara Mills", lines: 3 },
  { name: "Aegean Oils", lines: 3 },
  { name: "Cappadocia Pulses", lines: 2 },
  { name: "Bosphorus Pasta", lines: 6 },
];

function Widget({ icon: Icon, title, children, className = "", testid }) {
  return (
    <motion.div variants={fadeUp} className={`dm-card dm-card-hover p-5 ${className}`} data-testid={testid}>
      <div className="flex items-center gap-2 mb-4">
        <Icon className="h-4 w-4 text-blue-600" strokeWidth={1.75} />
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">{title}</span>
      </div>
      {children}
    </motion.div>
  );
}

export default function BuilderShowcase() {
  return (
    <section id="builder" className="py-24 sm:py-32" data-testid="builder-showcase-section">
      <div className="dm-container">
        <SectionHeader
          eyebrow="SmartContainer™ Builder"
          title="Your procurement operation, on one screen."
          description="Selected products, manufacturers, pallet allocation and container readiness — a single source of truth for the whole shipment."
        />
        <motion.div initial="hidden" whileInView="visible" viewport={viewport}
          transition={{ staggerChildren: 0.07 }} className="mt-16 dm-card p-4 sm:p-6">
          <div className="flex items-center justify-between px-1 pb-5 mb-1 border-b border-slate-100">
            <div className="flex items-center gap-2.5">
              <span className="grid place-items-center h-8 w-8 rounded-lg bg-slate-900">
                <Container className="h-4 w-4 text-white" strokeWidth={2} />
              </span>
              <div>
                <div className="text-sm font-bold text-slate-900">SmartContainer #SC-4821</div>
                <div className="text-[11px] text-slate-400">40HQ · Mersin → Rotterdam</div>
              </div>
            </div>
            <span className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-emerald-50 border border-emerald-200 px-3 py-1 text-[11px] font-semibold text-emerald-700">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
              Ready to consolidate
            </span>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 pt-5">
            <Widget icon={Boxes} title="Selected Products" className="col-span-2" testid="widget-products">
              <div className="space-y-2.5">
                {CONTAINER_PRODUCTS.map((p) => (
                  <div key={p.name} className="flex items-center gap-3">
                    <span className="h-2.5 w-2.5 rounded-[3px]" style={{ backgroundColor: p.color }} />
                    <span className="text-sm text-slate-700 flex-1">{p.name}</span>
                    <div className="w-24 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${p.pallets * 12}%`, backgroundColor: p.color }} />
                    </div>
                    <span className="text-xs font-medium text-slate-400 w-14 text-right">{p.pallets} pallets</span>
                  </div>
                ))}
              </div>
            </Widget>

            <Widget icon={Factory} title="Manufacturers" className="col-span-2" testid="widget-manufacturers">
              <div className="space-y-2.5">
                {MANUFACTURERS.map((m) => (
                  <div key={m.name} className="flex items-center gap-3">
                    <span className="grid place-items-center h-6 w-6 rounded-md bg-slate-100 text-[10px] font-bold text-slate-500">
                      {m.name.split(" ").map((w) => w[0]).join("")}
                    </span>
                    <span className="text-sm text-slate-700 flex-1">{m.name}</span>
                    <span className="text-xs font-medium text-slate-400">{m.lines} lines</span>
                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" strokeWidth={2} />
                  </div>
                ))}
              </div>
            </Widget>

            <Widget icon={Grid3x3} title="Pallet Allocation" testid="widget-pallets">
              <div className="grid grid-cols-6 gap-1">
                {Array.from({ length: 24 }).map((_, i) => (
                  <div key={i} className="aspect-square rounded-[3px]" style={{ backgroundColor: i < 18 ? "#2563eb" : "#e2e8f0" }} />
                ))}
              </div>
              <div className="mt-3 text-2xl font-extrabold text-slate-900">18<span className="text-sm font-semibold text-slate-400">/24</span></div>
            </Widget>

            <Widget icon={Container} title="Container Fill" testid="widget-fill">
              <div className="relative grid place-items-center py-1">
                <svg viewBox="0 0 100 100" className="h-24 w-24 -rotate-90">
                  <circle cx="50" cy="50" r="42" fill="none" stroke="#e2e8f0" strokeWidth="10" />
                  <motion.circle cx="50" cy="50" r="42" fill="none" stroke="#2563eb" strokeWidth="10"
                    strokeLinecap="round" strokeDasharray={2 * Math.PI * 42}
                    initial={{ strokeDashoffset: 2 * Math.PI * 42 }}
                    whileInView={{ strokeDashoffset: 2 * Math.PI * 42 * (1 - 0.82) }}
                    viewport={{ once: true }} transition={{ duration: 1.2, ease: "easeOut" }} />
                </svg>
                <span className="absolute text-xl font-extrabold text-slate-900">82%</span>
              </div>
            </Widget>

            <Widget icon={Weight} title="Estimated Weight" testid="widget-weight">
              <div className="text-3xl font-extrabold text-slate-900">24.6<span className="text-base font-semibold text-slate-400"> t</span></div>
              <div className="mt-1 text-xs text-slate-400">of 26.0 t payload limit</div>
              <div className="mt-3 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                <div className="h-full rounded-full bg-slate-900" style={{ width: "94%" }} />
              </div>
            </Widget>

            <Widget icon={Ruler} title="Estimated Volume" testid="widget-volume">
              <div className="text-3xl font-extrabold text-slate-900">62.4<span className="text-base font-semibold text-slate-400"> m³</span></div>
              <div className="mt-1 text-xs text-slate-400">of 76.0 m³ capacity</div>
              <div className="mt-3 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                <div className="h-full rounded-full bg-blue-600" style={{ width: "82%" }} />
              </div>
            </Widget>

            <Widget icon={Activity} title="Procurement Status" testid="widget-status">
              <ul className="space-y-2">
                {[["Quotes confirmed", true], ["Purchase orders", true], ["Production", false], ["Freight booked", false]].map(([label, done]) => (
                  <li key={label} className="flex items-center gap-2 text-sm">
                    <span className={`h-2 w-2 rounded-full ${done ? "bg-emerald-500" : "bg-amber-400"}`} />
                    <span className={done ? "text-slate-700" : "text-slate-400"}>{label}</span>
                  </li>
                ))}
              </ul>
            </Widget>

            <Widget icon={CheckCircle2} title="Container Readiness" testid="widget-readiness">
              <div className="flex flex-col items-start gap-2">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 border border-emerald-200 px-3 py-1 text-xs font-bold text-emerald-700">
                  <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={2.5} />Ready
                </span>
                <p className="text-xs text-slate-400 leading-relaxed">All manufacturers confirmed. Awaiting production completion before consolidation.</p>
              </div>
            </Widget>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
